// ============================================================================
// Per-URL visibility matching for the floating embed.
//
// Consumes the per-channel `visibility` rules from the remote `/config`
// response and decides whether the floating dock should render on the current
// `window.location.pathname`.
//
// Dependency-free on purpose: the embed ships as a single IIFE and bundle size
// matters, so we don't pull in `picomatch`/`minimatch`. The glob dialect is
// intentionally tiny — see `globToRegExp`.
// ============================================================================

/** One glob → action mapping evaluated against `window.location.pathname`. */
export interface VisibilityPattern {
	glob: string;
	action: "show" | "hide";
}

/**
 * One glob → "appear after" mapping. Orthogonal to {@link VisibilityPattern}:
 * it never affects whether the bar shows on a page, only *when* it appears on
 * pages where it's already visible. `appearAfter` is a CSS selector on the host
 * page; the bar holds back until that element is scrolled above the viewport.
 */
export interface AppearPattern {
	glob: string;
	appearAfter: string;
}

/**
 * Per-channel visibility rules. Mirrors the app-side `visibility` JSONB shape
 * (WAN-516). Semantics: show on all pages by default, override per-URL.
 *
 * The list of globs may arrive under either key: the app persists it as
 * `rules`, while older payloads / this SDK's original contract used
 * `patterns`. {@link patternsOf} reads whichever is present so a channel
 * configured in the dashboard takes effect without a data migration.
 */
export interface VisibilityRules {
	/** Action when no pattern matches the current path. */
	default: "show" | "hide";
	/** Glob patterns, in order. SDK contract key. */
	patterns?: VisibilityPattern[];
	/** Glob patterns, in order. Key the app/dashboard persists. */
	rules?: VisibilityPattern[];
	/**
	 * Per-URL "appear after" timing, in order. Independent of the show/hide
	 * patterns above: the first rule whose glob matches the current path holds
	 * the bar back until its `appearAfter` element is scrolled past. Pages with
	 * no matching rule keep the default appear-delay timer.
	 */
	appearRules?: AppearPattern[];
}

/** The glob list from a rules object, under whichever key it arrived. */
export function patternsOf(
	rules: VisibilityRules | null | undefined,
): VisibilityPattern[] {
	return rules?.patterns ?? rules?.rules ?? [];
}

// Sentinel that survives the single-`*` pass so we can translate `**` first
// without it being re-touched. Chosen to never collide with a real path.
const DOUBLE_STAR = "\0\0";

/**
 * Translate a path glob into an anchored RegExp.
 *
 * - `**` matches across segments (including `/`).
 * - `*` matches within a single segment (no `/`).
 * - Everything else is matched literally (regex metacharacters escaped).
 */
export function globToRegExp(glob: string): RegExp {
	// Escape regex metachars first (this also escapes `*`, which we undo next).
	const escaped = glob.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
	const translated = escaped
		// `\*\*` (escaped `**`) → cross-segment wildcard, via a placeholder.
		.replace(/\\\*\\\*/g, DOUBLE_STAR)
		// remaining `\*` (escaped single `*`) → within-segment wildcard.
		.replace(/\\\*/g, "[^/]*")
		.split(DOUBLE_STAR)
		.join(".*");
	return new RegExp(`^${translated}$`);
}

/** Whether `glob` matches `pathname`. A malformed glob never matches. */
export function matchGlob(glob: string, pathname: string): boolean {
	try {
		return globToRegExp(glob).test(pathname);
	} catch {
		return false;
	}
}

/**
 * Resolve whether the floating bar should be visible on `pathname`.
 *
 * - `null`/`undefined` rules → `true` (show everywhere). Keeps a new embed
 *   safe against servers that don't send the field yet, and an old embed safe
 *   against a server that does.
 * - Otherwise **hide always wins** (most restrictive): if any matching pattern
 *   hides, the bar is hidden — order-independent, so you can allowlist
 *   `/docs/**` (show) then carve out `/docs/internal` (hide). If no hide
 *   matches but a show matches, it's shown. If nothing matches, `rules.default`
 *   decides (the dashboard infers `default` from whether any show rule exists).
 */
export function isVisibleForPath(
	rules: VisibilityRules | null | undefined,
	pathname: string,
): boolean {
	if (!rules) {
		return true;
	}
	let matchedShow = false;
	for (const pattern of patternsOf(rules)) {
		if (matchGlob(pattern.glob, pathname)) {
			if (pattern.action === "hide") {
				return false; // hide always wins
			}
			matchedShow = true;
		}
	}
	if (matchedShow) {
		return true; // a show match beats the default
	}
	return (rules.default ?? "show") !== "hide";
}

/**
 * The "appear after" CSS selector for `pathname`, or `null` if none applies.
 *
 * Returns the `appearAfter` of the first {@link AppearPattern} whose glob
 * matches — order decides ties. `null` (no rules, or no match) means the caller
 * falls back to the default appear-delay timer. Only meaningful on paths where
 * {@link isVisibleForPath} is already true; a hidden page never renders the bar
 * regardless.
 */
export function appearTriggerForPath(
	rules: VisibilityRules | null | undefined,
	pathname: string,
): string | null {
	for (const pattern of rules?.appearRules ?? []) {
		if (matchGlob(pattern.glob, pathname)) {
			return pattern.appearAfter;
		}
	}
	return null;
}
